<template>
  <div>
    <v-app-bar app class="shell-top" clipped-left flat height="52">
      <button v-if="$store.state.axios && !unassigned" aria-label="Menü ein-/ausklappen" class="shell-ib" v-tip="'Menü ein-/ausklappen'"
              type="button" @click="$emit('update:showDrawer', !showDrawer)">
        <v-icon>mdi-menu</v-icon>
      </button>
      <router-link :to="homeLink" class="shell-logo" title="Zur Startseite">
        <span class="shell-logo__mark"><v-icon small>mdi-ice-cream</v-icon></span>
        Vanilla
      </router-link>
      <span v-if="environment && $vuetify.breakpoint.smAndUp" :title="`Verbunden mit ${apiPath}`" class="shell-env">
        {{ environment }}
      </span>
      <app-bar-debug-area/>
      <app-bar-search-control v-if="isLoggedIn && !$$user.isPartner && !portal"/>
      <v-spacer v-else/>
      <app-bar-call-center-agent-control v-if="isLoggedIn && !portal"/>
      <app-bar-create-menu v-if="isLoggedIn && $vuetify.breakpoint.smAndUp && !$$user.isPartner && !portal"/>
      <button v-if="isLoggedIn && canUseGmapsDistanceMatrix && $vuetify.breakpoint.smAndUp && !portal" aria-label="Kartensuche (Umkreis)"
              class="shell-ib" v-tip="'Kartensuche (Umkreis)'" type="button" @click="showGmapsSearchDialog = true">
        <v-icon>mdi-map-search-outline</v-icon>
      </button>
      <button v-if="isLoggedIn && !$$user.isPartner && $vuetify.breakpoint.smAndUp && !portal" :aria-label="stackTitle" v-tip="stackTitle" class="shell-ib"
              type="button" @click="showStackDialog = true">
        <v-icon>mdi-tray-full</v-icon>
        <span v-if="openStacks.length" class="shell-count shell-count--dot"/>
      </button>
      <app-bar-messenger v-if="isLoggedIn && $$user.isEmployee && !portal"/>
      <app-bar-notification-center v-if="isLoggedIn"/>
      <app-bar-profile-menu v-if="isLoggedIn" @show:createAvataaarDialog="showCreateAvataaarDialog = true"/>
      <!-- DIALOGS -->
      <create-avataaar-dialog v-if="isLoggedIn && !$$user.isPartner && !portal" v-model="showCreateAvataaarDialog" type="employee"/>
      <gmaps-search-dialog v-if="$store.state.axios && canUseGmapsDistanceMatrix" v-model="showGmapsSearchDialog"/>
      <stack-dialog v-if="$store.state.axios && !$$user.isPartner" v-model="showStackDialog" :active-stacks="openStacks"/>
    </v-app-bar>
    <!-- NOTIFICATIONS -->
    <div v-if="isLoggedIn" class="notification-area">
      <div class="notification-background"/>
      <app-notifications/>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import {ConfigHandlerAPI} from '@/classes/api/config-handler.api.class';
import {Config} from '@/interfaces/config.interface';
import AppBarSearchControl from '@/components/app/appBar/appBarSearchControl.vue';
import GmapsSearchDialog from '@/components/app/gmapsSearchDialog.vue';
import {CustomAccessKeys} from '@/classes/clientOnly/permissionTreeResources/enums/CustomAccessKeys';
import AppBarNotificationCenter
  from '@/components/app/appBar/notifications/notificationCenter/appBarNotificationCenter.vue';
import AppBarMessenger from '@/components/app/appBar/appBarMessenger.vue';
import {StackManagerApi} from '@/classes/api/stack-manager.api.class';
import {isObjectId, retrieveObjectId} from '@/helpers/isObjectId.helper';
import mongoose from 'mongoose';
import {StackItemStates} from '@/enums/StackItemStates.enum';
import {Stack} from '@/interfaces/stack.interface';
import AppBarDebugArea from '@/components/app/appBar/appBarDebugArea.vue';
import AppBarCallCenterAgentControl from '@/components/app/appBar/appBarCallCenterAgentControl.vue';
import AppBarCreateMenu from '@/components/app/appBar/appBarCreateMenu.vue';
import {environmentLabel} from '@/components/app/shell/shellNav';
import {PARTNER_HOME} from '@/router/routeAccess';
import {APPLICANT_PORTAL_HOME, hidesOfficeShell, NO_ACCESS_HOME, portalMode, TEACHER_PORTAL_HOME} from '@/helpers/portalAccess.helper';

export default Vue.extend({
  components: {
    AppBarCallCenterAgentControl,
    AppBarCreateMenu,
    AppBarDebugArea,
    AppBarMessenger,
    AppBarNotificationCenter,
    AppNotifications: () => import('@/components/app/appBar/notifications/appNotifications/appNotifications.vue'),
    CreateAvataaarDialog: () => import('@/components/common/profile/createAvataaarDialog.vue'),
    AppBarSearchControl,
    AppBarProfileMenu: () => import('@/components/app/appBar/appBarProfileMenu.vue'),
    StackDialog: import('@/components/stack/StackDialog.vue'),
    GmapsSearchDialog,
  },
  props: {
    showDrawer: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    showStackDialog: false,
    showCreateAvataaarDialog: false,
    showSearchInput: false,
    showUserMenu: true,
    showGmapsSearchDialog: false,
    loading: false,
    customerConfig: null as null | Config,
    openStacks: [] as Stack[],
  }),
  computed: {
    isLoggedIn(): boolean {
      return !!this.$store.state.axios;
    },
    /**
     * Lehrkraft-/Bewerbungs-Bereich (portal page or teacher-only / applicant login): same app bar, but only logo, bell and
     * profile menu – no search, „Neu“, Stapel, Kartensuche, Messenger, telephony
     */
    portal(): boolean {
      return !!this.$route.meta?.portal || hidesOfficeShell(portalMode());
    },
    /** Login without any role / menu key: no menu (nothing to open) */
    unassigned(): boolean {
      return portalMode() === 'unassigned';
    },
    homeLink(): string {
      if (this.$$user.isPartner) {
        return PARTNER_HOME;
      }
      const mode = portalMode();
      return mode === 'teacher' ? TEACHER_PORTAL_HOME : mode === 'applicant' ? APPLICANT_PORTAL_HOME : mode === 'unassigned' ? NO_ACCESS_HOME : '/';
    },
    apiPath(): string {
      return process.env.VUE_APP_API_PATH || '';
    },
    environment(): string {
      return environmentLabel(this.apiPath);
    },
    stackTitle(): string {
      return this.openStacks.length ? 'Offene Stapel zu diesem Profil' : 'Stapel';
    },
    canUseGmapsDistanceMatrix(): boolean {
      return !!this.$store.state.crudAccessManager.getCustom(CustomAccessKeys.USE_GMAPS_DISTANCE_MATRIX);
    },
  },
  methods: {
    async getConfig() {
      try {
        this.loading = true;
        // DO NOT TOUCH - BREAKING
        this.customerConfig = await ConfigHandlerAPI.getConfig();
      } finally {
        this.loading = false;
      }
    },
    async getStacks() {
      this.openStacks = [];
      const path = this.$route.path.split('/').filter((el) => el !== '');
      if (path.length >= 2 && this.isLoggedIn && !this.$$user.isPartner && !this.portal) {
        const entity = path[0];
        const id = path[1];
        if (!['parents', 'students', 'employees', 'institutions'].includes(entity) || !isObjectId(id)) {
          return;
        }
        const stackResp = await StackManagerApi.find({
          filter: {
            isClosed: false,
            meta: [id as mongoose.Types.ObjectId],
          },
        });
        if (stackResp.stacks.length > 0) {
          const openStacks = [];
          for (const stack of stackResp.stacks) {
            const found = stack.items.filter((el) => {
              const metaId = retrieveObjectId(el.meta);
              return metaId?.toString() === id.toString() && ![StackItemStates.DONE, StackItemStates.MUTED].includes(el.state);
            });
            if (found.length > 0) {
              openStacks.push(stack);
            }
          }
          this.openStacks = openStacks;
        }

      }
    },
  },
  watch: {
    '$route.path': {
      immediate: true,
      handler() {
        this.getStacks();
      },
    },
    '$store.state.user': {
      deep: true,
      handler() {
        this.showUserMenu = false;
        setTimeout(() => {
          this.showUserMenu = true;
        }, 500);
      },
    },
  },
  beforeDestroy() {
    this.openStacks = [];
  },
});
</script>

<style scoped>
.notification-area {
  z-index: 4;
  position: fixed;
  right: 0;
  top: 52px;
  max-height: calc(100vh - 52px);
}

/* Only decoration: must never catch the clicks meant for the pop-ups ("Ansehen", "Alle als gelesen markieren") */
.notification-area > .notification-background {
  pointer-events: none;
  position: absolute;
  top: 5px;
  right: 0;
  left: 30px;
  bottom: 10px;
  filter: blur(20px);
}

/* Follows the app theme (Hell / Dunkel / System), not only the operating system */
.notification-area > .notification-background {
  background-color: rgba(230, 230, 255, 0.50);
}

.theme--dark .notification-area > .notification-background {
  background-color: rgba(20, 20, 40, 0.5);
}

</style>
